/**
 * useNotifications.js — state/data owner for the Notifications page.
 *
 * Colocated with the page rather than in src/hooks/ because it is not reusable
 * plumbing: it owns exactly this page's list, filters, stats, its unread tally,
 * and its two mutations. The page above it stays presentational — filter grid,
 * type icons and row layout — and never touches fetch.
 *
 * Mirrors the shape of the admin feed's own useNotifications.js (same filter
 * keys, same stats shape, same page/setPage contract) so the two pages can share
 * a layout, but stays independent in three ways the portal endpoint requires:
 *
 *  1. REST IS THE SOURCE OF TRUTH. `items`/`unreadCount`/`stats` always come from
 *     /api/portal/notifications[/stats], which the backend scopes to the caller's
 *     own userId (and their assigned robots). Socket traffic can only ever
 *     trigger a refetch (see useNotificationsSocket) — it is never rendered
 *     directly and never merged into this state, because the socket layer has no
 *     authentication and nothing it delivers can be trusted as "addressed to
 *     this user".
 *
 *  2. OPTIMISTIC MARK-READ. Both mutations paint the new state immediately and
 *     fall back to a silent refetch if the request fails, so a failed write can
 *     never leave the row looking read while the server still has it unread.
 *
 *  3. NO scope FILTER. Which tenant a row belongs to is an admin-only concept —
 *     a customer's feed is already scoped to their own rows server-side, so
 *     exposing it here would be a filter over data the user never sees anyway.
 *     `roverType`/`serialID` ARE offered, but mean something narrower than on
 *     the admin feed: the server resolves both against this user's assigned
 *     robots, so they pick among "my robots" rather than across the fleet.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  listNotifications,
  getNotificationStats,
  markNotificationRead,
  markAllNotificationsRead,
} from "../../services/notifications.api";
import { NOTIFICATIONS_LOAD_ERROR_MESSAGE } from "../../utils/errorMessages";

// The header's bell badge owns its own unread count with no shared state, so a
// mutation made here has to announce itself for the badge to stay in sync
// instead of waiting out the header's 30s poll.
const UNREAD_CHANGED_EVENT = "botriq:notifications-unread-changed";
const notifyUnreadChanged = () => window.dispatchEvent(new Event(UNREAD_CHANGED_EVENT));

// Matches the backend default (portalNotifications.controller.js) — stated here so
// the request is explicit rather than relying on the server's default staying put.
const PAGE_SIZE = 30;

export const EMPTY_FILTERS = {
  type: "",
  severity: "",
  // Both resolved server-side against this user's OWN assigned robots, so they
  // only narrow what ownership already scoped — `serialID` here means "one of
  // my robots", not the admin feed's fleet-wide sense.
  roverType: "",
  serialID: "",
  unreadOnly: "",
  q: "",
  from: "",
  to: "",
};

// Blank values must not be sent — an empty `?type=` is not a filter, it is noise
// in the query string.
const compactParams = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== "" && v != null));

const useNotifications = () => {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    unread: 0,
    info: 0,
    success: 0,
    warning: 0,
    critical: 0,
  });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Serialized so the fetch effect depends on a primitive and doesn't re-run on
  // every render just because `filters` is a fresh object identity.
  const filterKey = JSON.stringify(filters);

  // Monotonic request id: a response whose id is no longer the latest belongs to a
  // superseded fetch and is dropped instead of overwriting fresher data.
  const requestIdRef = useRef(0);

  const fetchNotifications = useCallback(
    async ({ silent = false } = {}) => {
      const requestId = ++requestIdRef.current;
      if (!silent) setLoading(true);
      try {
        const parsed = JSON.parse(filterKey);
        // Settled independently: the list is the page's primary data and stats
        // are a secondary enhancement (the header tiles). A stats failure — the
        // endpoint being down, still deploying, whatever — must never blank out
        // an otherwise-successful list.
        const [listRes, statRes] = await Promise.allSettled([
          listNotifications(compactParams({ ...parsed, page, limit: PAGE_SIZE })),
          getNotificationStats(compactParams(parsed)),
        ]);
        if (requestIdRef.current !== requestId) return;

        if (listRes.status === "fulfilled") {
          setItems(listRes.value.notifications);
          setTotal(listRes.value.total);
          setUnreadCount(listRes.value.unreadCount);
          setError(null);
        } else if (!silent) {
          setError(NOTIFICATIONS_LOAD_ERROR_MESSAGE);
        }

        if (statRes.status === "fulfilled") setStats(statRes.value || {});
      } catch {
        if (requestIdRef.current !== requestId) return;
        // A silent refetch is a background reconcile (a socket signal, or a failed
        // mutation being rolled back). Surfacing an error banner for it would
        // replace a perfectly good list with a failure the user did not ask for.
        if (!silent) setError(NOTIFICATIONS_LOAD_ERROR_MESSAGE);
      } finally {
        if (requestIdRef.current === requestId && !silent) setLoading(false);
      }
    },
    [filterKey, page]
  );

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  /** Refetch without dropping the current list into a loading state. */
  const refresh = useCallback(() => fetchNotifications({ silent: true }), [fetchNotifications]);

  const markRead = useCallback(
    async (id) => {
      const wasUnread = items.some((n) => n._id === id && !n.isRead);
      if (!wasUnread) return;

      setItems((prev) => prev.map((n) => (n._id === id ? { ...n, isRead: true } : n)));
      setUnreadCount((c) => Math.max(0, c - 1));
      setStats((s) => ({ ...s, unread: Math.max(0, (s.unread || 0) - 1) }));

      try {
        await markNotificationRead(id);
        // Only announce once the server has actually applied the write — the
        // header's handler re-reads /unread-count immediately on this event,
        // and firing it before the PUT lands would have that GET race the PUT
        // and win, overwriting the badge with the still-stale server count
        // (it then only self-corrects on the next 30s poll or socket ping,
        // which read as the badge being "one step behind").
        notifyUnreadChanged();
      } catch {
        // The optimistic paint is now a lie — let the server decide what the row
        // actually looks like rather than guessing at a revert.
        refresh();
        notifyUnreadChanged();
      }
    },
    [items, refresh]
  );

  const markAllRead = useCallback(async () => {
    const snapshot = items;
    setItems((prev) => prev.map((n) => (n.isRead ? n : { ...n, isRead: true })));
    setUnreadCount(0);
    setStats((s) => ({ ...s, unread: 0 }));

    try {
      await markAllNotificationsRead();
      // See markRead's comment: announce only after the server confirms the
      // write, so the header's refetch cannot land before it and re-show a
      // stale nonzero count.
      notifyUnreadChanged();
    } catch {
      setItems(snapshot);
      refresh();
      notifyUnreadChanged();
    }
  }, [items, refresh]);

  // Changing a filter resets to page 1 — a narrower filter can otherwise land the
  // user on a page that no longer exists and show an empty list.
  //
  // `from`/`to` additionally keep each other in range: picking a "From" after the
  // current "To" (or a "To" before the current "From") pulls the other bound up
  // to match, rather than silently sending the server a reversed range that would
  // just return zero rows with no indication why.
  const setFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "from" && value && next.to && value > next.to) next.to = value;
      if (key === "to" && value && next.from && value < next.from) next.from = value;
      return next;
    });
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setPage(1);
  }, []);

  const hasActiveFilters = useMemo(() => Object.values(filters).some((v) => v !== ""), [filters]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return {
    items,
    stats,
    filters,
    setFilter,
    resetFilters,
    hasActiveFilters,
    page,
    setPage,
    total,
    totalPages,
    pageSize: PAGE_SIZE,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    refresh,
    markRead,
    markAllRead,
  };
};

export default useNotifications;
